import React from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLang } from "@/i18n";

const DATA = {
  fr: {
    title: "Ils nous font confiance",
    subtitle: "Depuis 2015, des centaines de professionnels belges roulent et travaillent grâce à I-LEASE.",
    items: [
      { name: "Marc D.", role: "Transport Dubois · Charleroi", text: "Réponse ultra rapide et un accompagnement humain de A à Z. Mon camion était financé en moins de 48h. Je recommande à 100%." },
      { name: "Sophie L.", role: "Indépendante · Namur", text: "Olivier a trouvé la meilleure formule de leasing pour ma voiture de société. Des conseils clairs, sans jargon. Un vrai partenaire." },
      { name: "Ahmed B.", role: "BTP Construct · Liège", text: "Financement d'une pelleteuse pour mon chantier : simple, efficace et au meilleur taux. Enfin un leaseur qui comprend le génie civil." },
    ],
  },
  nl: {
    title: "Zij vertrouwen ons",
    subtitle: "Sinds 2015 rijden en werken honderden Belgische professionals dankzij I-LEASE.",
    items: [
      { name: "Marc D.", role: "Transport Dubois · Charleroi", text: "Ultrasnel antwoord en een menselijke begeleiding van A tot Z. Mijn vrachtwagen was in minder dan 48u gefinancierd. Een aanrader!" },
      { name: "Sophie L.", role: "Zelfstandige · Namen", text: "Olivier vond de beste leasingformule voor mijn bedrijfswagen. Duidelijk advies, zonder jargon. Een echte partner." },
      { name: "Ahmed B.", role: "BTP Construct · Luik", text: "Financiering van een graafmachine voor mijn werf: eenvoudig, efficiënt en aan de beste rentevoet. Eindelijk een leasegever die civiele techniek begrijpt." },
    ],
  },
  en: {
    title: "They trust us",
    subtitle: "Since 2015, hundreds of Belgian professionals drive and work thanks to I-LEASE.",
    items: [
      { name: "Marc D.", role: "Transport Dubois · Charleroi", text: "Ultra-fast answer and a human support from A to Z. My truck was financed in under 48h. Highly recommend!" },
      { name: "Sophie L.", role: "Self-employed · Namur", text: "Olivier found the best leasing formula for my company car. Clear advice, no jargon. A true partner." },
      { name: "Ahmed B.", role: "BTP Construct · Liège", text: "Financing an excavator for my site: simple, efficient and at the best rate. Finally a lessor who understands civil engineering." },
    ],
  },
};

export const Testimonials = () => {
  const { lang } = useLang();
  const d = DATA[lang] || DATA.fr;
  const GOOGLE_REVIEW_URL = "https://www.google.com/search?q=I-LEASE+SRL+Limal+avis";
  const RATING = "4.9";
  const badgeLabel = { fr: "Avis vérifiés Google", nl: "Geverifieerde Google-reviews", en: "Verified Google reviews" }[lang] || "Avis vérifiés Google";
  const reviewLabel = { fr: "Laissez-nous un avis", nl: "Laat een review achter", en: "Leave us a review" }[lang] || "Laissez-nous un avis";
  return (
    <section className="relative py-24 sm:py-32 max-w-7xl mx-auto px-5 sm:px-8" data-testid="testimonials">
      <div className="mb-14 text-center max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" strokeWidth={0} />)}
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-bold">{d.title}</h2>
        <p className="mt-4 text-muted-foreground text-lg">{d.subtitle}</p>

        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="google-review-badge"
          className="mt-7 inline-flex items-center gap-3 rounded-full border border-white/10 bg-card pl-4 pr-5 py-2.5 hover:border-accent/40 hover:-translate-y-0.5 transition-all group">
          <svg className="w-5 h-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          <span className="flex items-center gap-1.5 text-sm">
            <span className="font-display font-bold text-white">{RATING}</span>
            <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" strokeWidth={0} />)}</span>
            <span className="text-muted-foreground ml-1 group-hover:text-white transition-colors">· {badgeLabel}</span>
          </span>
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {d.items.map((it, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.12 }}
            className="relative rounded-2xl border border-white/10 bg-card p-7 hover:border-accent/30 transition-colors" data-testid={`testimonial-${i}`}>
            <Quote className="w-9 h-9 text-accent/30 mb-4" strokeWidth={1.5} />
            <p className="text-sm text-gray-200 leading-relaxed">"{it.text}"</p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-accent/20 flex items-center justify-center font-display font-bold text-accent">
                {it.name.charAt(0)}
              </div>
              <div>
                <p className="font-display font-semibold text-sm">{it.name}</p>
                <p className="text-xs text-muted-foreground">{it.role}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn"
          className="inline-flex items-center gap-2.5 rounded-full bg-white text-black hover:bg-accent hover:text-white transition-colors px-7 py-3.5 text-base font-medium group">
          <svg className="w-5 h-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          {reviewLabel}
        </a>
      </div>
    </section>
  );
};
